/**
 * The register's current basket (T07-23): server-side (`/pos/carts*`) — every change returns the
 * whole basket with the server's totals / 内消費税 / warnings, which replaces what is shown. The
 * basket id is kept in Dexie (`carts`) per register so a reload or the lock screen never loses it.
 *
 * - scan → a "確認中…" row at once (from the offline copy when the card is in it) → the server
 *   confirms (🔔) or refuses with the reason in plain words (low tone);
 * - SSE: a card sold elsewhere turns red at once and the pay buttons close (`soldElsewhere`);
 * - a manager's approval (price, > 10 % discount, typed line) is asked by `withApproval`.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { ApprovalCancelled, useApproval } from "@/app/approval";
import { useAuthState, usePos } from "@/app/context";
import { useToast } from "@/components/Toast";
import { call } from "@/lib/api";
import { findInSnapshot, type SnapshotItem } from "@/lib/db";
import { errorText, isApiError } from "@/lib/errors";
import type { InventoryChange } from "@/lib/sse";
import type { Cart, CustomerAttach, DiscountAdd, LineAdd, LinePatch } from "@/lib/types";

import { useAvailabilityStream } from "./hooks/useAvailabilityStream";
import { unavailableText, type UnavailableInfo } from "./labels";

export interface PendingLine {
  key: string;
  code: string;
  item: SnapshotItem | null;
  status: "confirming" | "failed";
  error?: string;
  at: number;
}

export interface CartApi {
  cartId: string | null;
  cart: Cart | undefined;
  loading: boolean;
  pending: PendingLine[];
  /** Lines whose card was sold / taken elsewhere (from the live stream). */
  soldElsewhere: ReadonlySet<string>;
  /** When each sold-elsewhere line went (ISO) — 「10:02に売れました」 (T07-40). */
  soldAt: ReadonlyMap<string, string>;
  /** Pay buttons open: the server says the basket can be paid and nothing is sold elsewhere. */
  canPay: boolean;
  blockers: string[];
  scan(code: string): Promise<void>;
  addVariant(input: { variantId: string; unitId?: string | null; qty?: number }): Promise<void>;
  setQty(lineId: string, qty: number): Promise<void>;
  removeLine(lineId: string): Promise<void>;
  overridePrice(lineId: string, price: number, reason: string): Promise<boolean>;
  resetPrice(lineId: string): Promise<void>;
  addDiscount(body: DiscountAdd): Promise<boolean>;
  removeDiscount(discountId: string): Promise<void>;
  attachCustomer(body: CustomerAttach): Promise<boolean>;
  detachCustomer(): Promise<void>;
  /** The basket's メモ (T07-41/42 `PATCH /pos/carts/{id}`, If-Match); empty = removed. */
  setNote(note: string): Promise<boolean>;
  clear(): Promise<void>;
  recheck(): Promise<void>;
  /** After a sale: forget this basket and start a new one. */
  startNew(): Promise<void>;
  /** Make another basket of this register the current one (a resumed 保留, an exchange). */
  openCart(cartId: string): Promise<void>;
  refresh(): void;
}

export const CartContext = createContext<CartApi | null>(null);

export const cartKey = (id: string | null) => ["pos-carts", "detail", id] as const;

const FAILED_ROW_MS = 4_000;

/** No basket could be opened for a scan (the server did not answer) — the row says so. */
class CartNotReady extends Error {
  constructor() {
    super("cart not ready");
    this.name = "CartNotReady";
  }
}

export function CartProvider({ children, enabled }: { children: ReactNode; enabled: boolean }) {
  const { api, db, sounds } = usePos();
  const state = useAuthState();
  const registerId =
    state.kind === "locked" || state.kind === "unlocked" ? state.device.registerId : null;
  const signedIn = state.kind === "unlocked";
  const queryClient = useQueryClient();
  const { withApproval } = useApproval();
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const [cartId, setCartId] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingLine[]>([]);
  const [sold, setSold] = useState<ReadonlySet<string>>(() => new Set());
  const [soldAt, setSoldAt] = useState<ReadonlyMap<string, string>>(() => new Map());
  const seq = useRef(0);

  // -- the basket id: Dexie → the server (an open basket of this register, else a new one) --
  const ensureCart = useCallback(async (): Promise<string | null> => {
    if (!registerId) {
      return null;
    }
    const stored = await db.carts.get(registerId);
    if (stored) {
      try {
        const cart = await call(() =>
          api.GET("/v1/pos/carts/{cart_id}", { params: { path: { cart_id: stored.cartId } } }),
        );
        if (cart.status === "open" || cart.status === "paying") {
          queryClient.setQueryData(cartKey(cart.id), cart);
          return cart.id;
        }
      } catch (error) {
        if (!isApiError(error) || (error.status !== 404 && error.code !== "cart_not_found")) {
          throw error;
        }
      }
    }
    const created = await call(() => api.POST("/v1/pos/carts", { body: {} }));
    queryClient.setQueryData(cartKey(created.id), created);
    await db.carts.put({
      registerId,
      cartId: created.id,
      version: created.version,
      updatedAt: Date.now(),
    });
    return created.id;
  }, [api, db, queryClient, registerId]);

  // one basket request at a time: the effect below and a scan that comes before the basket
  // exists (a scanner right after the PIN — T07-37 on the real stack) share it
  const creating = useRef<Promise<string | null> | null>(null);
  const ensureOnce = useCallback((): Promise<string | null> => {
    creating.current ??= ensureCart().finally(() => {
      creating.current = null;
    });
    return creating.current;
  }, [ensureCart]);

  useEffect(() => {
    if (!enabled || !signedIn || cartId) {
      return;
    }
    let cancelled = false;
    ensureOnce()
      .then((id) => {
        if (!cancelled) {
          setCartId(id);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          toast.show(errorText(error, t), "error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, signedIn, cartId, ensureOnce, toast, t]);

  const query = useQuery({
    queryKey: cartKey(cartId),
    queryFn: () =>
      call(() =>
        api.GET("/v1/pos/carts/{cart_id}", { params: { path: { cart_id: cartId ?? "" } } }),
      ),
    enabled: Boolean(cartId) && signedIn,
    staleTime: 10_000,
  });
  const cart = query.data;

  // lines that left the basket are no longer "sold elsewhere"
  const soldNow = useMemo<ReadonlySet<string>>(() => {
    if (!cart || sold.size === 0) {
      return sold;
    }
    const ids = new Set(cart.lines.map((l) => l.id));
    return new Set([...sold].filter((id) => ids.has(id)));
  }, [cart, sold]);

  // a basket sold / emptied elsewhere (another screen, the offline upload): start a new one
  useEffect(() => {
    if (cart && (cart.status === "committed" || cart.status === "abandoned")) {
      void (async () => {
        if (registerId) {
          await db.carts.delete(registerId);
        }
        setCartId(null);
      })();
    }
  }, [cart, db, registerId]);

  const put = useCallback(
    (next: Cart) => {
      queryClient.setQueryData(cartKey(next.id), next);
      if (registerId) {
        void db.carts.put({
          registerId,
          cartId: next.id,
          version: next.version,
          updatedAt: Date.now(),
        });
      }
    },
    [db, queryClient, registerId],
  );

  const fail = useCallback(
    (error: unknown) => {
      if (error instanceof ApprovalCancelled) {
        toast.show(t("approval.cancelled"));
        return;
      }
      if (isApiError(error, "version_conflict")) {
        void queryClient.invalidateQueries({ queryKey: cartKey(cartId) });
      }
      toast.show(errorText(error, t), "error");
    },
    [cartId, queryClient, t, toast],
  );

  /** One change of the basket (answer = the whole basket); false when it did not happen. */
  const change = useCallback(
    async (
      send: (id: string, approval?: string) => Promise<Cart>,
      amount?: number | null,
    ): Promise<boolean> => {
      if (!cartId) {
        return false;
      }
      try {
        const next = await withApproval((token) => send(cartId, token), {
          subject: cartId,
          amount: amount ?? null,
        });
        put(next);
        return true;
      } catch (error) {
        fail(error);
        return false;
      }
    },
    [cartId, fail, put, withApproval],
  );

  const approvalHeader = (token?: string): Record<string, string> =>
    token ? { "X-Approval-Token": token } : {};
  const ifMatch = (): Record<string, string> => (cart ? { "If-Match": String(cart.version) } : {});

  const addLine = useCallback(
    async (body: LineAdd, key: string): Promise<boolean> => {
      try {
        // scanned before the basket was ready: wait for it instead of dropping the scan
        const id = cartId ?? (await ensureOnce());
        if (!id) {
          throw new CartNotReady();
        }
        if (!cartId) {
          setCartId(id);
        }
        const next = await withApproval(
          (token) =>
            call(() =>
              api.POST("/v1/pos/carts/{cart_id}/lines", {
                params: { path: { cart_id: id } },
                body,
                headers: approvalHeader(token),
              }),
            ),
          { subject: id },
        );
        put(next);
        sounds.play("ok");
        setPending((list) => list.filter((p) => p.key !== key));
        return true;
      } catch (error) {
        sounds.play("fail");
        const text =
          error instanceof ApprovalCancelled
            ? t("approval.cancelled")
            : error instanceof CartNotReady
              ? t("sale.cart_not_ready")
              : isApiError(error, "line_sold_elsewhere") || isApiError(error, "insufficient_stock")
                ? unavailableText(error.detail as UnavailableInfo, t, i18n.resolvedLanguage ?? "ja")
                : errorText(error, t);
        setPending((list) =>
          list.map((p) => (p.key === key ? { ...p, status: "failed", error: text } : p)),
        );
        setTimeout(() => setPending((list) => list.filter((p) => p.key !== key)), FAILED_ROW_MS);
        return false;
      }
    },
    [api, cartId, ensureOnce, put, sounds, t, i18n, withApproval],
  );

  const scan = useCallback(
    async (code: string) => {
      const value = code.trim();
      if (!value) {
        return;
      }
      seq.current += 1;
      const key = `scan-${String(seq.current)}`;
      const row: PendingLine = {
        key,
        code: value,
        item: null,
        status: "confirming",
        at: performance.now(),
      };
      setPending((list) => [...list, row]);
      // the offline copy shows the card at once while the server checks it
      void findInSnapshot(db, value).then((item) => {
        if (item) {
          setPending((list) => list.map((p) => (p.key === key ? { ...p, item } : p)));
        }
      });
      await addLine({ code: value, qty: 1 }, key);
    },
    [addLine, db],
  );

  const addVariant = useCallback(
    async (input: { variantId: string; unitId?: string | null; qty?: number }) => {
      seq.current += 1;
      const key = `add-${String(seq.current)}`;
      const item = (await db.snapshotItems.get(input.variantId)) ?? null;
      setPending((list) => [
        ...list,
        { key, code: input.variantId, item, status: "confirming", at: performance.now() },
      ]);
      await addLine(
        {
          variantId: input.variantId,
          ...(input.unitId ? { unitId: input.unitId } : {}),
          qty: input.qty ?? 1,
        },
        key,
      );
    },
    [addLine, db],
  );

  const patch = useCallback(
    (lineId: string, changes: Partial<LinePatch>) =>
      change((id, token) =>
        call(() =>
          api.PATCH("/v1/pos/carts/{cart_id}/lines/{line_id}", {
            params: { path: { cart_id: id, line_id: lineId } },
            body: { clearOverride: false, remove: false, ...changes },
            headers: { ...ifMatch(), ...approvalHeader(token) },
          }),
        ),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ifMatch reads the current cart
    [api, change, cart?.version],
  );

  const recheck = useCallback(async () => {
    if (!cartId) {
      return;
    }
    try {
      const result = await call(() =>
        api.GET("/v1/pos/carts/{cart_id}/recheck", { params: { path: { cart_id: cartId } } }),
      );
      put(result.cart);
    } catch {
      void queryClient.invalidateQueries({ queryKey: cartKey(cartId) });
    }
  }, [api, cartId, put, queryClient]);

  const onSold = useCallback(
    (lineIds: string[], change: InventoryChange) => {
      setSold((current) => new Set([...current, ...lineIds]));
      // T07-40: the red line says when — the event's time, else the moment the register heard it
      const at = change.occurredAt ?? new Date().toISOString();
      setSoldAt((current) => {
        const next = new Map(current);
        for (const id of lineIds) {
          if (!next.has(id)) {
            next.set(id, at);
          }
        }
        return next;
      });
      sounds.play("fail");
    },
    [sounds],
  );

  useAvailabilityStream({
    cart,
    enabled: Boolean(cartId) && signedIn,
    onSold,
    onCartChanged: () => void queryClient.invalidateQueries({ queryKey: cartKey(cartId) }),
    onRecheck: () => void recheck(),
  });

  const startNew = useCallback(async () => {
    if (registerId) {
      await db.carts.delete(registerId);
    }
    setPending([]);
    setSold(new Set());
    setSoldAt(new Map());
    setCartId(null);
  }, [db, registerId]);

  const openCart = useCallback(
    async (id: string) => {
      if (registerId) {
        await db.carts.put({ registerId, cartId: id, version: 0, updatedAt: Date.now() });
      }
      setPending([]);
      setSold(new Set());
      setSoldAt(new Map());
      await queryClient.invalidateQueries({ queryKey: cartKey(id) });
      setCartId(id);
    },
    [db, queryClient, registerId],
  );

  const value = useMemo<CartApi>(() => {
    const blockers = [...(cart?.blockers ?? [])];
    if (soldNow.size > 0) {
      blockers.unshift("sold_elsewhere");
    }
    const confirming = pending.some((p) => p.status === "confirming");
    return {
      cartId,
      cart,
      loading: query.isLoading || (!cartId && signedIn && enabled),
      pending,
      soldElsewhere: soldNow,
      soldAt,
      canPay: Boolean(cart?.canCheckout) && soldNow.size === 0 && !confirming,
      blockers,
      scan,
      addVariant,
      setQty: async (lineId, qty) => {
        await patch(lineId, { qty });
      },
      removeLine: async (lineId) => {
        await change((id) =>
          call(() =>
            api.DELETE("/v1/pos/carts/{cart_id}/lines/{line_id}", {
              params: { path: { cart_id: id, line_id: lineId } },
            }),
          ),
        );
      },
      overridePrice: (lineId, price, reason) =>
        patch(lineId, { overridePrice: price, overrideReason: reason }),
      resetPrice: async (lineId) => {
        await patch(lineId, { clearOverride: true });
      },
      addDiscount: (body) =>
        change(
          (id, token) =>
            call(() =>
              api.POST("/v1/pos/carts/{cart_id}/discounts", {
                params: { path: { cart_id: id } },
                body,
                headers: approvalHeader(token),
              }),
            ),
          body.type === "amount" ? body.value : null,
        ),
      removeDiscount: async (discountId) => {
        await change((id) =>
          call(() =>
            api.DELETE("/v1/pos/carts/{cart_id}/discounts/{discount_id}", {
              params: { path: { cart_id: id, discount_id: discountId } },
            }),
          ),
        );
      },
      attachCustomer: (body) =>
        change((id) =>
          call(() =>
            api.POST("/v1/pos/carts/{cart_id}/customer", {
              params: { path: { cart_id: id } },
              body,
            }),
          ),
        ),
      detachCustomer: async () => {
        await change((id) =>
          call(() =>
            api.DELETE("/v1/pos/carts/{cart_id}/customer", { params: { path: { cart_id: id } } }),
          ),
        );
      },
      setNote: (note) =>
        change((id) =>
          call(() =>
            api.PATCH("/v1/pos/carts/{cart_id}", {
              params: { path: { cart_id: id } },
              body: { note: note.trim() || null },
              headers: ifMatch(),
            }),
          ),
        ),
      clear: async () => {
        const ok = await change((id) =>
          call(() => api.DELETE("/v1/pos/carts/{cart_id}", { params: { path: { cart_id: id } } })),
        );
        if (ok) {
          await startNew();
        }
      },
      recheck,
      startNew,
      openCart,
      refresh: () => void queryClient.invalidateQueries({ queryKey: cartKey(cartId) }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- helpers close over the listed state
  }, [
    cart,
    cartId,
    pending,
    sold,
    soldAt,
    query.isLoading,
    signedIn,
    enabled,
    scan,
    addVariant,
    patch,
    change,
    recheck,
    startNew,
    openCart,
    api,
    queryClient,
  ]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
